/**
 * Supported UI languages — shared Hub/Portal SSOT.
 *
 * Two identifiers exist for every language and conflating them is the recurring
 * bug: the theme config and the Hub pickers store a **short ISO code** (`fr`),
 * while the customer portal's `LanguageContext` and every `Intl` call key on a
 * **BCP 47 tag** (`fr-FR`). Cross a boundary without normalising and the saved
 * brand default is silently dropped for the portal's own default. Derive both
 * from here rather than writing a fourth copy of the map.
 *
 * A language is not a market. Currency, tax institutions and statutory concepts
 * follow the operator's market and live in config; only wording belongs here.
 */

export interface SupportedLanguage {
  /** Short ISO 639-1 code — what theme config and the Hub pickers store. */
  code: string;
  /** BCP 47 tag — what the portal, `Intl` and the preview iframes key on. */
  bcp47: string;
  /** Endonym, shown in customer-facing switchers. */
  nativeName: string;
  /** English name, shown in Hub authoring surfaces. */
  englishName: string;
  /** Emoji flag for the language's primary market. */
  flag: string;
}

/**
 * Ordered for display: the three original languages first, then the 2026
 * multi-market rollout alphabetically by English name.
 */
export const SUPPORTED_LANGUAGES = Object.freeze([
  { code: 'en', bcp47: 'en-GB', nativeName: 'English', englishName: 'English', flag: '🇬🇧' },
  { code: 'pt', bcp47: 'pt-PT', nativeName: 'Português', englishName: 'Portuguese', flag: '🇵🇹' },
  { code: 'fr', bcp47: 'fr-FR', nativeName: 'Français', englishName: 'French', flag: '🇫🇷' },
  { code: 'cs', bcp47: 'cs-CZ', nativeName: 'Čeština', englishName: 'Czech', flag: '🇨🇿' },
  { code: 'da', bcp47: 'da-DK', nativeName: 'Dansk', englishName: 'Danish', flag: '🇩🇰' },
  { code: 'nl', bcp47: 'nl-NL', nativeName: 'Nederlands', englishName: 'Dutch', flag: '🇳🇱' },
  { code: 'fi', bcp47: 'fi-FI', nativeName: 'Suomi', englishName: 'Finnish', flag: '🇫🇮' },
  { code: 'de', bcp47: 'de-DE', nativeName: 'Deutsch', englishName: 'German', flag: '🇩🇪' },
  { code: 'el', bcp47: 'el-GR', nativeName: 'Ελληνικά', englishName: 'Greek', flag: '🇬🇷' },
  { code: 'hu', bcp47: 'hu-HU', nativeName: 'Magyar', englishName: 'Hungarian', flag: '🇭🇺' },
  { code: 'it', bcp47: 'it-IT', nativeName: 'Italiano', englishName: 'Italian', flag: '🇮🇹' },
  { code: 'nb', bcp47: 'nb-NO', nativeName: 'Norsk bokmål', englishName: 'Norwegian', flag: '🇳🇴' },
  { code: 'sk', bcp47: 'sk-SK', nativeName: 'Slovenčina', englishName: 'Slovak', flag: '🇸🇰' },
  { code: 'es', bcp47: 'es-ES', nativeName: 'Español', englishName: 'Spanish', flag: '🇪🇸' },
  { code: 'sv', bcp47: 'sv-SE', nativeName: 'Svenska', englishName: 'Swedish', flag: '🇸🇪' },
] as const satisfies readonly SupportedLanguage[]);

/** Short code of a supported language, as a union — use to key exhaustive tables. */
export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

/** System default — matches `theme.schema.json`'s declared default. */
export const DEFAULT_LANGUAGE_CODE = 'en';
export const DEFAULT_LANGUAGE_BCP47 = 'en-GB';

export const SUPPORTED_LANGUAGE_CODES: readonly LanguageCode[] = Object.freeze(SUPPORTED_LANGUAGES.map((l) => l.code));
export const SUPPORTED_LANGUAGE_BCP47: readonly string[] = Object.freeze(SUPPORTED_LANGUAGES.map((l) => l.bcp47));

const BY_CODE = new Map<string, SupportedLanguage>(SUPPORTED_LANGUAGES.map((l) => [l.code, l]));
const BY_BCP47 = new Map<string, SupportedLanguage>(SUPPORTED_LANGUAGES.map((l) => [l.bcp47.toLowerCase(), l]));

/**
 * Resolve either identifier to its language. Accepts a short code (`fr`), an
 * exact tag (`fr-FR`), or an unlisted region of a supported language (`fr-BE`,
 * `de-AT`) — the region is dropped, since wording follows the language and the
 * market's own facts come from config. Returns `undefined` if unsupported.
 */
export function findLanguage(value: string | undefined | null): SupportedLanguage | undefined {
  if (!value) return undefined;
  return BY_BCP47.get(value.toLowerCase()) ?? BY_CODE.get(value.toLowerCase().split('-')[0]);
}

/** Normalise either identifier to BCP 47, falling back to the system default. */
export const toBcp47 = (value: string | undefined | null): string => findLanguage(value)?.bcp47 ?? DEFAULT_LANGUAGE_BCP47;

/** Normalise either identifier to a short ISO code, falling back to the system default. */
export const toLanguageCode = (value: string | undefined | null): string => findLanguage(value)?.code ?? DEFAULT_LANGUAGE_CODE;

export const isSupportedLanguage = (value: string | undefined | null): boolean => findLanguage(value) !== undefined;

/**
 * A table that must carry every supported language. Prefer this over
 * `Record<string, T>` for translated copy: adding a language to the registry
 * then fails the build at each table still missing it, rather than silently
 * degrading that surface to English.
 */
export type ByLanguage<T> = Record<LanguageCode, T>;

/**
 * Read a language-keyed table with an arbitrary runtime locale (`pt-PT`, `de-AT`,
 * a stored preference), falling back to English. `ByLanguage` is deliberately not
 * string-indexable — that is what makes it exhaustive — so callers holding a loose
 * locale string come through here.
 */
export const forLanguage = <T>(table: ByLanguage<T>, locale: string | undefined | null): T =>
  table[(findLanguage(locale)?.code ?? DEFAULT_LANGUAGE_CODE) as LanguageCode];
